import { Spin } from 'antd';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Navigate, useSearchParams } from 'react-router-dom';

import { workflowsApi } from '@/api/workflows';
import { SUPPORTED_LANGUAGES } from '@/i18n';

/**
 * Landing point for an advisor arriving with `applicationType` on the URL
 * (bmo-workflow-platform-spec.md:35, forwarded through ISAM login by
 * AuthEndpoints.BuildContextRedirectUrl). `applicationType` is the workflow's
 * `appName` and is the only param required to resolve a target — it's
 * resolved to a template id and the advisor bounces straight into a
 * FormShell runtime, the /advisor/dashboard picker never mounting on this
 * path. `applicationId` (a specific advisor-client application instance)
 * takes precedence when present — resume that exact application. It's
 * nullable: when absent, the resolver still redirects, into a freshly
 * minted application on the resolved workflow via the existing `/new` mint
 * route, rather than bailing out to the picker. Falls back to the picker
 * only when `applicationType` itself is missing or can't be resolved.
 *
 * TODO(BMO-ADVISOR-INTEGRATION): resolution is a client-side `appName` match
 * against `workflowsApi.list()` (the same originator-list endpoint + eligibility
 * filter `AdvisorDashboard` uses). Swap for a real advisor-scoped lookup once
 * one exists — see docs/pretend-advisor.md swap-point #2.
 */
export function AdvisorEntryPage() {
  const [searchParams] = useSearchParams();
  const { i18n } = useTranslation();
  const applicationType = searchParams.get('applicationType');
  const applicationId = searchParams.get('applicationId');
  const mode = searchParams.get('mode');
  const language = searchParams.get('language');

  // undefined = still resolving; null = fall back to the picker; string = navigate there.
  const [target, setTarget] = useState<string | null | undefined>(undefined);

  useEffect(() => {
    if (language && (SUPPORTED_LANGUAGES as readonly string[]).includes(language)) {
      void i18n.changeLanguage(language);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (!applicationType) {
      setTarget(null);
      return;
    }
    let cancelled = false;
    (async () => {
      try {
        const list = await workflowsApi.list();
        if (cancelled) return;
        const match = list.find(
          (w) =>
            w.appName.toLowerCase() === applicationType.toLowerCase() &&
            w.status === 'Approved' &&
            w.isActive === true,
        );
        if (!match) {
          setTarget(null);
          return;
        }
        // applicationId takes precedence when present (resume that exact
        // application); when absent, still redirect — into a fresh
        // application on this workflow via the existing mint route rather
        // than falling back to the picker.
        const dest = applicationId
          ? `/advisor/workflows/${match.id}/applications/${applicationId}`
          : `/advisor/workflows/${match.id}/applications/new`;
        setTarget(mode ? `${dest}?mode=${encodeURIComponent(mode)}` : dest);
      } catch {
        if (!cancelled) setTarget(null);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [applicationType, applicationId, mode]);

  if (target === undefined) {
    return (
      <div style={{ display: 'flex', justifyContent: 'center', padding: 48 }}>
        <Spin size="large" />
      </div>
    );
  }

  return <Navigate to={target ?? '/advisor/dashboard'} replace />;
}
